import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {isEmpty} from '@shared/helpers/lang'

import {selectCardPromotionGroup} from '@web/actions/cardPromotion'
import {
    changeRechargePoint,
    selectComplexPayMethod,
    selectNormalPayMethodTab,
    selectMobilePayMethod,
    setMaximumRechargePoint,
    selectVoucherMethod,
    cancelEasyMobileRegistration,
    SelectPaymentMethodActions,
} from '@web/actions/paymentMethod'
import {
    changeDeliveryAreaExtraFee,
    changeAlwaysUsesAllNaverPoint,
    changeNaverPoint,
    changePayLaterPoint,
    enablePayLaterPoint,
    enablePayLaterPointByAllPoint,
    updatePointPayment,
    resetNaverPoint,
    resetPayLaterPoint,
    usingAllNaverPoint,
    usingAllPayLaterPoint,
    changePointAndToggle,
} from '@web/actions/pointPayment'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {GeneralMobileCode, GeneralSkip, PayMethod, PayMethodType, PayMethodVoucher, Service} from '@web/core/constants'
import {selectLastPaymentMethod, unselectRechargePayMethod} from '@web/slices/paymentMethod/slice'

import type {PaymentMethodReducers, MakeSliceOptions} from './interfaces'
import type {PaymentMethodState} from '@web/slices/paymentMethod/slice'

function getPayNextMethod(state: PaymentMethodState, payMethods: PayMethodType[]) {
    if (
        state.selected.payNextMethod !== GeneralSkip.VIRTUAL_ACCOUNT &&
        payMethods.includes(PayMethod.VIRTUAL_ACCOUNT)
    ) {
        return GeneralSkip.VIRTUAL_ACCOUNT
    }
    return state.selected.payNextMethod
}

export default (options?: MakeSliceOptions) =>
    createSlice<PaymentMethodState, PaymentMethodReducers, 'paymentMethod', SliceSelectors<PaymentMethodState>>({
        name: 'paymentMethod',
        initialState: {
            availableTypes: [],
            defaultPayMethod: PayMethod.NONE,
            defaultNormalPayMethod: null,
            selected: {
                payMethods: [],
                prevPayMethod: PayMethod.NONE,
                payNextMethod: GeneralSkip.VIRTUAL_ACCOUNT,
            },
            complex: {
                selectionType: 'CONCURRENT',
                availableService: false,
            },
            statements: {},
            errorExposureArea: [],
            shopBill: {
                resultType: null,
                authToken: '',
                svcType: Service.C1,
                cpId: '',
                itemId: '',
                sellerId: '',
                title: '',
            },
            verifyServerClientData: '',
            scene: 'IDLE',
        },
        reducers: {
            selectLastPaymentMethod: (state: PaymentMethodState) => {
                const {
                    selected: {payMethods, prevPayMethod},
                } = state

                return {
                    ...state,
                    selected: {
                        ...state.selected,
                        payMethods: [
                            ...payMethods.filter(
                                (payMethod: PayMethodType) => payMethod !== state.selected.prevPayMethod,
                            ),
                            prevPayMethod,
                        ],
                    },
                }
            },
        },
        extraReducers: (builder) => {
            builder
                .addCase(selectMobilePayMethod, (state, {payload}) => {
                    const selectPayMethod =
                        GeneralMobileCode.MOBILE_PIN === payload ? PayMethod.MOBILE_EASY_PAY : PayMethod.MOBILE
                    state.selected.prevPayMethod = selectPayMethod
                    state.selected.payMethods = [
                        ...state.selected.payMethods.filter((payMethod) => payMethod === PayMethod.RECHARGE_POINT),
                        selectPayMethod,
                    ]
                })
                .addCase(selectVoucherMethod, (state, {payload}) => {
                    const selectPayMethod =
                        PayMethodVoucher.CULTURE_LAND === payload ? PayMethod.CULTURE_LAND : PayMethod.HAPPY_MONEY
                    state.selected.prevPayMethod = selectPayMethod
                    state.selected.payMethods = [
                        ...state.selected.payMethods.filter((payMethod) => payMethod === PayMethod.RECHARGE_POINT),
                        selectPayMethod,
                    ]
                })
                .addCase(selectLastPaymentMethod, (state: PaymentMethodState) => {
                    const {
                        selected: {payMethods, prevPayMethod},
                    } = state

                    state.selected.payMethods = payMethods.filter(
                        (payMethod: PayMethodType) => payMethod !== state.selected.prevPayMethod,
                    )
                    state.selected.payNextMethod = getPayNextMethod(state, state.selected.payMethods)
                    state.selected.prevPayMethod = prevPayMethod
                })
                .addCase(unselectRechargePayMethod, (state) => {
                    const payMethodsByUnRecharge = state.selected.payMethods.filter(
                        (payMethod) => payMethod !== PayMethod.RECHARGE_POINT,
                    )
                    const payMethods = isEmpty(payMethodsByUnRecharge)
                        ? [state.defaultPayMethod]
                        : payMethodsByUnRecharge
                    state.selected.payMethods = payMethods
                    state.selected.payNextMethod = getPayNextMethod(state, payMethods)

                    const prevPayMethod = payMethods.find((payMethod) => payMethod !== PayMethod.RECHARGE_POINT)
                    if (prevPayMethod) {
                        state.selected.prevPayMethod = prevPayMethod
                    }
                })
                .addCase(cancelEasyMobileRegistration, (state) => {
                    // 간편결제 해지시 모바일 일반결제 선택
                    const payMethods = [
                        ...state.selected.payMethods.filter((payMethod) => payMethod !== PayMethod.MOBILE_EASY_PAY),
                        PayMethod.MOBILE,
                    ]
                    state.selected.payMethods = payMethods
                    const prevPayMethod = payMethods.find((payMethod) => payMethod !== PayMethod.RECHARGE_POINT)
                    if (prevPayMethod) {
                        state.selected.prevPayMethod = prevPayMethod
                    }
                })
                .addCase(selectCardPromotionGroup.fulfilled, (state) => {
                    state.selected.payMethods = [PayMethod.CARD_EASY_PAY]
                    state.selected.prevPayMethod = PayMethod.CARD_EASY_PAY
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher(
                    isAnyOf(
                        changeRechargePoint.fulfilled,
                        setMaximumRechargePoint.fulfilled,
                        selectComplexPayMethod.fulfilled,
                        selectNormalPayMethodTab.fulfilled,
                        ...SelectPaymentMethodActions,
                    ),
                    (state, {payload}) => {
                        // 패밀리결제 사용시 복합결제 금지
                        if (payload.selectedPayMethod === PayMethod.FAMILY_PAY) {
                            state.selected.payMethods = [PayMethod.FAMILY_PAY]
                            return
                        }
                        const payMethods = isEmpty(payload.payMethods) ? [state.defaultPayMethod] : payload.payMethods
                        state.selected.payMethods = payMethods
                        state.selected.payNextMethod = getPayNextMethod(state, payMethods)

                        const prevPayMethod = payMethods.find((payMethod) => payMethod !== PayMethod.RECHARGE_POINT)
                        if (prevPayMethod) {
                            state.selected.prevPayMethod = prevPayMethod
                        }
                    },
                )
                .addMatcher(
                    isAnyOf(
                        changeDeliveryAreaExtraFee.fulfilled,
                        changeNaverPoint.fulfilled,
                        resetNaverPoint.fulfilled,
                        usingAllNaverPoint.fulfilled,
                        changeAlwaysUsesAllNaverPoint.fulfilled,
                        changePayLaterPoint.fulfilled,
                        changePointAndToggle.fulfilled,
                        resetPayLaterPoint.fulfilled,
                        usingAllPayLaterPoint.fulfilled,
                        enablePayLaterPoint.fulfilled,
                        enablePayLaterPointByAllPoint.fulfilled,
                        updatePointPayment.fulfilled,
                    ),
                    (state, {payload}) => {
                        if (payload.paymentAmount > 0) {
                            const payMethods = isEmpty(payload.payMethods)
                                ? [state.defaultPayMethod]
                                : payload.payMethods
                            state.selected.payMethods = payMethods
                            state.selected.payNextMethod = getPayNextMethod(state, payMethods)

                            const prevPayMethod = payMethods.find((payMethod) => payMethod !== PayMethod.RECHARGE_POINT)
                            if (prevPayMethod) {
                                state.selected.prevPayMethod = prevPayMethod
                            }
                        }
                    },
                )
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.paymentMethod ?? state
                })
        },
    })
